import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Layout, Result, Skeleton, Space } from 'antd';
import type { InstitutionContext as InstCtx } from '@velora/contracts';
import { ApiError, productApi } from '../api';

const Ctx = createContext<InstCtx | null>(null);

/**
 * Wraps every `/i/:institution/*` route. Fetches the institution + org + session
 * from /api/product/institutions/:slug at boot. Children can call useInstitution().
 * Rules:
 *   - Loading: skeleton (fast; no chrome jumps)
 *   - 404: friendly page, offer to go home
 *   - Other errors: alert with the message
 */
export function InstitutionProvider({ children }: { children: ReactNode }) {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['institution', institution],
    queryFn: () => productApi.getInstitution(institution),
    enabled: !!institution,
    staleTime: 60_000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <Layout style={{ minHeight: '100vh', padding: 24 }}>
        <Space direction="vertical" style={{ maxWidth: 720 }} size="middle">
          <Skeleton.Input active style={{ width: 260 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
        </Space>
      </Layout>
    );
  }
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) {
      return (
        <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            status="404"
            title="Institution not found"
            subTitle={
              <span>
                <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{institution}</code>{' '}
                does not exist. Check the link, or go to <a href="/">the landing page</a>.
              </span>
            }
          />
        </Layout>
      );
    }
    return (
      <Layout style={{ minHeight: '100vh', padding: 24 }}>
        <Alert type="error" showIcon message="Could not load institution" description={String(q.error)} />
      </Layout>
    );
  }
  if (!q.data) return null;

  return <Ctx.Provider value={q.data}>{children}</Ctx.Provider>;
}

export function useInstitution(): InstCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useInstitution called outside <InstitutionProvider>');
  return v;
}
